/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
import React, {lazy, Suspense} from 'react';
import {BrowserRouter,Routes,Route,Navigate} from 'react-router-dom';
import {Provider} from 'react-redux';
import {store} from './store/store';
import ProtectedRoute from './components/common/ProtectedRoute';
import { loadLocaleFromServer } from './utils/locale';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PatientPage from './pages/PatientPage';
import PatientRegistrationPage from './pages/PatientRegistrationPage';
import AppointmentPage from './pages/AppointmentPage';
import AppointmentBookingPage from './pages/AppointmentBookingPage';
import BranchManagementPage from './pages/BranchManagementPage';
import TokenQueuePage from './pages/TokenQueuePage';
import UserStationMappingPage from './pages/UserStationMappingPage';
import OPDPage from './pages/OPDPage';
import IPDPage from './pages/IPDPage';
import IPDAdmissionPage from './pages/IPDAdmissionPage';
import IPDDischargePage from './pages/IPDDischargePage';
import IPDTransferPage from './pages/IPDTransferPage';
import WardBedSetupPage from './pages/WardBedSetupPage';
import ICUPage from './pages/ICUPage';
import EmergencyPage from './pages/EmergencyPage';
import EmergencyRegistrationPage from './pages/EmergencyRegistrationPage';
import OphthalmologyPage from './pages/OphthalmologyPage';
import MaternityPage from './pages/MaternityPage';
import RenalCarePage from './pages/RenalCarePage';
import MentalHealthPage from './pages/MentalHealthPage';
import PalliativeCarePage from './pages/PalliativeCarePage';
import CrmPage from './pages/CrmPage';
import MortuaryPage from './pages/MortuaryPage';
import MortuaryBodyPage from './pages/MortuaryBodyPage';
import MortuaryAutopsyPage from './pages/MortuaryAutopsyPage';
import MortuaryCase360Page from './pages/MortuaryCase360Page';
import MortuaryIotPage from './pages/MortuaryIotPage';
import MortuaryServicesPage from './pages/MortuaryServicesPage';
import MortuaryPortalPage from './pages/MortuaryPortalPage';
import OTPage from './pages/OTPage';
import OTSurgeryDetailPage from './pages/OTSurgeryDetailPage';
import BillingPage from './pages/BillingPage';
import BillDetailPage from './pages/BillDetailPage';
import PharmacyPage from './pages/PharmacyPage';
import LaboratoryPage from './pages/LaboratoryPage';
import LabResultEntryPage from './pages/LabResultEntryPage';
import RadiologyPage from './pages/RadiologyPage';
import RadiologyWorkflowPage from './pages/RadiologyWorkflowPage';
import BloodBankPage from './pages/BloodBankPage';
import DonorRegistrationPage from './pages/DonorRegistrationPage';
import InventoryPage from './pages/InventoryPage';
import NursingPage from './pages/NursingPage';
import DietPage from './pages/DietPage';
import HRMSPage from './pages/HRMSPage';
import BiomedicalPage from './pages/BiomedicalPage';
import TelemedicinePage from './pages/TelemedicinePage';
import CommunicationsPage from './pages/CommunicationsPage';
import ReportsPage from './pages/ReportsPage';
import WorkflowPage from './pages/WorkflowPage';
import ApprovalWorkflowsPage from './pages/ApprovalWorkflowsPage';
import PatientPortalPage from './pages/PatientPortalPage';
import NotificationPage from './pages/NotificationPage';
import AuditPage from './pages/AuditPage';
import VendorPage from './pages/VendorPage';
import CPOEPage from './pages/CPOEPage';
import DMSPage from './pages/DMSPage';
import SettingsPage from './pages/SettingsPage';
import AuditLogPage from './pages/AuditLogPage';
import DoctorSchedulePage from './pages/DoctorSchedulePage';
import ChargeMasterPage from './pages/ChargeMasterPage';
// NABH / India accreditation UI removed — quality lives under /quality (Kenya HMIS).
import FHIRPage from './pages/FHIRPage';
import TerminologyPage from './pages/TerminologyPage';
import DoctorDashboardPage from './pages/DoctorDashboardPage';
import DoctorEMRPage from './pages/DoctorEMRPage';
import NursingIndentPage from './pages/NursingIndentPage';
import DischargeSummaryPage from './pages/DischargeSummaryPage';
import PayerManagementPage from './pages/PayerManagementPage';
import InsurancePage from './pages/InsurancePage';
import EMARPage from './pages/EMARPage';
import FinancePage from './pages/FinancePage';
import AnalyticsPage from './pages/AnalyticsPage';
import ConsentPage from './pages/ConsentPage';
import FrontOfficeBillingPage from './pages/FrontOfficeBillingPage';
import DutyRosterPage from './pages/DutyRosterPage';
import SystemMastersPage from './pages/SystemMastersPage';
import LabParameterMasterPage from './pages/LabParameterMasterPage';
import LabReferenceRangesPage from './pages/LabReferenceRangesPage';
import LabCultureTemplatePage from './pages/LabCultureTemplatePage';
import LabReportMasterPage from './pages/LabReportMasterPage';
import CorporateMasterPage from './pages/CorporateMasterPage';
import MasterDataPage from './pages/MasterDataPage';
import LocationMasterPage from './pages/LocationMasterPage';
import CssdPage from './pages/CssdPage';
import SHAPage from './pages/SHAPage';
import ShrPage from './pages/ShrPage';
import SHAClaimPage from './pages/SHAClaimPage';
import SHAPreAuthPage from './pages/SHAPreAuthPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SessionsPage from './pages/SessionsPage';
import SystemHealthPage from './pages/SystemHealthPage';
import BackupPage from './pages/BackupPage';

const ExecutiveDashboardPage = lazy(()=>import('./pages/ExecutiveDashboardPage'));
const BedBoardPage = lazy(()=>import('./pages/BedBoardPage'));
const InsurancePreAuthPage = lazy(()=>import('./pages/InsurancePreAuthPage'));
const QualityIndicatorsPage = lazy(()=>import('./pages/QualityIndicatorsPage'));
const MOH505Page = lazy(()=>import('./pages/MOH505Page'));
const MohRegistersPage = lazy(()=>import('./pages/MohRegistersPage'));

const P=(C:React.ComponentType)=><ProtectedRoute><C/></ProtectedRoute>;

/** Share URLs opened on the UI host must not fall through to /dashboard → /login. */
function PublicShrShare() {
  const path = window.location.pathname + window.location.search;
  const api = process.env.REACT_APP_API_URL || '';
  let href = path;
  if (api.startsWith('http')) {
    try { href = new URL(api).origin + path; } catch { /* keep path */ }
  }
  React.useEffect(() => {
    if (href.startsWith('http') && !href.startsWith(window.location.origin)) window.location.replace(href);
  }, [href]);
  return (
    <div className="min-h-screen bg-slate-50 p-8 text-slate-800">
      <h1 className="text-lg font-bold">FHIR share link</h1>
      <p className="mt-2 text-sm max-w-xl">This address is a token-gated API link. It does not use the hospital login. The secret in the path is the access key and it expires on its own.</p>
      <p className="mt-4"><a className="text-teal-800 underline break-all" href={href}>{href.startsWith('http') ? href : 'Open on the API host (republish the share from Kenya SHR so the link includes http://…)'}</a></p>
    </div>
  );
}

export default function App(){
  // Pull this deployment's real country/currency/language/tax settings once on boot (see
  // utils/locale.ts) — fails silently and keeps the cached/default copy if the localization
  // migration hasn't run, so this never blocks first paint. GET /settings/localization requires
  // auth, so this only fires when a token already exists — calling it unconditionally (as before)
  // meant every visit to the Login screen fired a 401 that the api.ts interceptor turned into a
  // hard reload, which re-mounted App and fired the same call again: an infinite reload loop that
  // also exhausted the API's rate limit (the "429 Too Many Requests" + repeated "unauthorized"
  // errors reported in production).
  React.useEffect(() => { if (localStorage.getItem('accessToken')) loadLocaleFromServer(); }, []);
  return(
    <Provider store={store}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/login" element={<LoginPage/>}/>
          <Route path="/mortuary-portal" element={<MortuaryPortalPage/>}/>
          <Route path="/reset-password" element={<ResetPasswordPage/>}/>
          <Route path="/api/shr/share/*" element={<PublicShrShare/>}/>
          <Route path="/change-password" element={P(ChangePasswordPage)}/>
          <Route path="/sessions" element={P(SessionsPage)}/>
          <Route path="/system-health" element={P(SystemHealthPage)}/>
          <Route path="/backup" element={P(BackupPage)}/>
          <Route path="/dashboard" element={P(DashboardPage)}/>
          <Route path="/patients/register" element={P(PatientRegistrationPage)}/>
          <Route path="/patients/edit/:id" element={P(PatientRegistrationPage)}/>
          <Route path="/patients/*" element={P(PatientPage)}/>
          <Route path="/appointments/book" element={P(AppointmentBookingPage)}/>
          <Route path="/appointments/queue" element={P(TokenQueuePage)}/>
          <Route path="/appointments/*" element={P(AppointmentPage)}/>
          <Route path="/doctor/dashboard" element={P(DoctorDashboardPage)}/>
          <Route path="/doctor/emr" element={P(DoctorEMRPage)}/>
          <Route path="/opd/*" element={P(OPDPage)}/>
          <Route path="/ophthalmology" element={P(OphthalmologyPage)}/>
          <Route path="/maternity" element={P(MaternityPage)}/>
          <Route path="/renal-care" element={P(RenalCarePage)}/>
          <Route path="/mental-health" element={P(MentalHealthPage)}/>
          <Route path="/palliative-care" element={P(PalliativeCarePage)}/>
          <Route path="/crm" element={P(CrmPage)}/>
          <Route path="/mortuary/bodies/:id/case" element={P(MortuaryCase360Page)}/>
          <Route path="/mortuary/bodies/:id/autopsy" element={P(MortuaryAutopsyPage)}/>
          <Route path="/mortuary/bodies/:id" element={P(MortuaryBodyPage)}/>
          <Route path="/mortuary/scan/:token" element={P(MortuaryBodyPage)}/>
          <Route path="/mortuary/iot" element={P(MortuaryIotPage)}/>
          <Route path="/mortuary/services" element={P(MortuaryServicesPage)}/>
          <Route path="/mortuary/chapel" element={P(MortuaryServicesPage)}/>
          <Route path="/mortuary/funeral" element={P(MortuaryServicesPage)}/>
          <Route path="/mortuary/transport" element={P(MortuaryServicesPage)}/>
          <Route path="/mortuary/cemetery" element={P(MortuaryServicesPage)}/>
          <Route path="/mortuary/crematorium" element={P(MortuaryServicesPage)}/>
          <Route path="/mortuary/final-disposition" element={P(MortuaryServicesPage)}/>
          <Route path="/mortuary/*" element={P(MortuaryPage)}/>
          <Route path="/ipd/admit" element={P(IPDAdmissionPage)}/>
          <Route path="/ipd/discharge/:admissionId" element={P(IPDDischargePage)}/>
          <Route path="/ipd/transfer/:admissionId" element={P(IPDTransferPage)}/>
          <Route path="/ipd/ward-bed-setup" element={P(WardBedSetupPage)}/>
          <Route path="/ipd/*" element={P(IPDPage)}/>
          <Route path="/icu/*" element={P(ICUPage)}/>
          <Route path="/emergency/register" element={P(EmergencyRegistrationPage)}/>
          <Route path="/emergency/*" element={P(EmergencyPage)}/>
          <Route path="/ot/surgery/:id" element={P(OTSurgeryDetailPage)}/>
          <Route path="/ot/*" element={P(OTPage)}/>
          <Route path="/billing/bill/:id" element={P(BillDetailPage)}/>
          <Route path="/billing/*" element={P(BillingPage)}/>
          <Route path="/payers/*" element={P(PayerManagementPage)}/>
          <Route path="/insurance/*" element={P(InsurancePage)}/>
          <Route path="/emar/*" element={P(EMARPage)}/>
          <Route path="/finance/*" element={P(FinancePage)}/>
          <Route path="/consents/*" element={P(ConsentPage)}/>
          <Route path="/pharmacy/*" element={P(PharmacyPage)}/>
          <Route path="/laboratory/results/:id" element={P(LabResultEntryPage)}/>
          <Route path="/laboratory/*" element={P(LaboratoryPage)}/>
          <Route path="/radiology/order/:id" element={P(RadiologyWorkflowPage)}/>
          <Route path="/radiology/*" element={P(RadiologyPage)}/>
          <Route path="/bloodbank/register-donor" element={P(DonorRegistrationPage)}/>
          <Route path="/bloodbank/*" element={P(BloodBankPage)}/>
          <Route path="/inventory/*" element={P(InventoryPage)}/>
          <Route path="/nursing/*" element={P(NursingPage)}/>
          <Route path="/indents/*" element={P(NursingIndentPage)}/>
          <Route path="/discharge-summary" element={P(DischargeSummaryPage)}/>
          <Route path="/diet/*" element={P(DietPage)}/>
          <Route path="/hrms/*" element={P(HRMSPage)}/>
          <Route path="/biomedical/*" element={P(BiomedicalPage)}/>
          <Route path="/telemedicine/*" element={P(TelemedicinePage)}/>
          <Route path="/communications/*" element={P(CommunicationsPage)}/>
          <Route path="/reports/*" element={P(ReportsPage)}/>
          <Route path="/settings/*" element={P(SettingsPage)}/>
          <Route path="/settings/branches" element={P(BranchManagementPage)}/>
          <Route path="/settings/user-stations" element={P(UserStationMappingPage)}/>
          <Route path="/audit-log" element={P(AuditLogPage)}/>
          <Route path="/doctor-schedule" element={P(DoctorSchedulePage)}/>
          <Route path="/masters" element={P(MasterDataPage)}/>
          <Route path="/location-master" element={P(LocationMasterPage)}/>
          <Route path="/charge-master" element={P(ChargeMasterPage)}/>
          <Route path="/fhir/*" element={P(FHIRPage)}/>
          <Route path="/terminology/*" element={P(TerminologyPage)}/>
          <Route path="/workflow/*" element={P(WorkflowPage)}/>
          <Route path="/approvals/*" element={P(ApprovalWorkflowsPage)}/>
          <Route path="/patient-portal/*" element={P(PatientPortalPage)}/>
          <Route path="/notifications/*" element={P(NotificationPage)}/>
          <Route path="/audit/*" element={P(AuditPage)}/>
          <Route path="/vendors/*" element={P(VendorPage)}/>
          <Route path="/cpoe/*" element={P(CPOEPage)}/>
          <Route path="/dms/*" element={P(DMSPage)}/>
          <Route path="/analytics" element={P(AnalyticsPage)}/>
          <Route path="/front-office-billing/*" element={P(FrontOfficeBillingPage)}/>
              <Route path="/roster"                element={P(DutyRosterPage)}/>
              <Route path="/system-masters"         element={P(SystemMastersPage)}/>
              <Route path="/lab/parameter-master"   element={P(LabParameterMasterPage)}/>
              <Route path="/lab/reference-ranges"     element={P(LabReferenceRangesPage)}/>
              <Route path="/lab/culture-templates"   element={P(LabCultureTemplatePage)}/>
              <Route path="/lab/report-master"       element={P(LabReportMasterPage)}/>
              <Route path="/corporate"              element={P(CorporateMasterPage)}/>
              <Route path="/executive-dashboard"    element={P(ExecutiveDashboardPage)}/>
              <Route path="/bed-board"              element={P(BedBoardPage)}/>
              <Route path="/insurance-preauth"      element={P(InsurancePreAuthPage)}/>
              <Route path="/quality"                element={P(QualityIndicatorsPage)}/>
              <Route path="/moh505"                 element={P(MOH505Page)}/>
              <Route path="/moh"                    element={P(MohRegistersPage)}/>
              <Route path="/moh/:code"              element={P(MohRegistersPage)}/>
              <Route path="/cssd/*" element={P(CssdPage)}/>
              <Route path="/sha" element={P(SHAPage)}/>
              <Route path="/shr" element={P(ShrPage)}/>
              <Route path="/sha/new-claim" element={P(SHAClaimPage)}/>
              <Route path="/sha/claims/new" element={P(SHAClaimPage)}/>
              <Route path="/sha/claims/:id" element={P(SHAClaimPage)}/>
              <Route path="/sha/preauths" element={P(SHAPreAuthPage)}/>
          <Route path="*" element={<Navigate to="/dashboard" replace/>}/>
        </Routes>
      </BrowserRouter>
    </Provider>
  );
}
